import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChatCircleDots, Bug, Sparkle, Lightbulb, Wrench, CircleNotch, Check, ArrowLeft, Rocket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const TYPES = [
  { k: "bug",        label: "Bug Report",     icon: Bug,      desc: "Something broken? Tell me how to reproduce it." },
  { k: "suggestion", label: "Suggestion",     icon: Lightbulb,desc: "Small tweak that would improve your workflow." },
  { k: "feature",    label: "Feature Request",icon: Sparkle,  desc: "Idea for a brand-new capability or scanner." },
  { k: "general",    label: "General",        icon: Wrench,   desc: "Anything else — feedback, questions, praise." },
];

export default function Feedback() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [type, setType] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Login guard — push to /login if not authenticated
  useEffect(() => {
    if (!loading && !user) nav("/login", { replace: true });
  }, [loading, user, nav]);

  // If user navigated here from somewhere inside the app, capture the
  // referring page so we can attach it to the feedback (helps me debug
  // "this button doesn't work on the foo page" reports).
  const referrer = location.state?.from || (typeof document !== "undefined" ? document.referrer : "") || "";

  const submit = async (e) => {
    e?.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are both required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        type,
        subject: subject.trim(),
        message: message.trim(),
        page: referrer || window.location.pathname,
      });
      setDone(true);
      toast.success("Thanks — your feedback has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit feedback. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <CircleNotch size={20} className="animate-spin text-muted-fg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-6"
        >
          <ArrowLeft size={11} weight="bold" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <ChatCircleDots size={20} weight="fill" className="text-pos" />
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">Send Feedback</h1>
          <Link
            to="/roadmap"
            data-testid="feedback-roadmap-link"
            className="ml-auto px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:border-pos/60 hover:text-pos transition flex items-center gap-1.5"
          >
            <Rocket size={11} weight="fill" />
            See Roadmap
          </Link>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-8 max-w-xl">
          Found a bug, want a feature, or just have an idea? Drop it below — I personally
          read every message and reply to most within 24 hrs. Already-planned items live on
          the{" "}
          <Link to="/roadmap" className="text-pos hover:underline">public roadmap</Link> —
          upvote there if you want it sooner.
        </p>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-pos/40 bg-pos-soft/15 rounded-sm p-6 space-y-3"
            data-testid="feedback-success"
          >
            <div className="flex items-center gap-2">
              <Check size={18} weight="bold" className="text-pos" />
              <span className="font-medium text-primary-fg">Got it — thank you!</span>
            </div>
            <p className="text-sm text-secondary-fg">
              Your {TYPES.find((t) => t.k === type)?.label.toLowerCase()} has landed in my inbox.
              I'll get back to you on <span className="text-pos">{user.email}</span> if I need
              more details.
            </p>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setDone(false); setSubject(""); setMessage(""); }}
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:border-pos/60 hover:text-pos"
                data-testid="feedback-submit-another"
              >
                Send another
              </button>
              <Link
                to="/dashboard"
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-pos text-pos rounded-sm hover:bg-pos-soft/40"
              >
                Back to Scanner
              </Link>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="feedback-form">
            {/* Type selector */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">
                What's this about?
              </div>
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = type === t.k;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setType(t.k)}
                      data-testid={`feedback-type-${t.k}`}
                      className={
                        "text-left p-3 border rounded-sm transition " +
                        (active
                          ? "border-pos bg-pos-soft/15"
                          : "border-subtle hover:border-pos/40 hover:bg-surface-hover")
                      }
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon size={13} weight={active ? "fill" : "regular"} className={active ? "text-pos" : "text-secondary-fg"} />
                        <span className={"text-xs font-medium " + (active ? "text-pos" : "text-primary-fg")}>
                          {t.label}
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-fg leading-snug">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={140}
                placeholder="A short summary"
                required
                data-testid="feedback-subject"
                className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm text-primary-fg placeholder-muted-fg/60 focus:outline-none focus:border-pos transition"
              />
              <div className="text-[9px] text-muted-fg/70 text-right mt-1">{subject.length}/140</div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">
                Details
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                rows={7}
                placeholder={
                  type === "bug"
                    ? "What did you do? What did you expect? What actually happened? Include URL / screenshot if possible."
                    : "Tell me more — context, why it matters, anything specific."
                }
                required
                data-testid="feedback-message"
                className="w-full bg-surface border border-subtle rounded-sm px-3 py-2 text-sm text-primary-fg placeholder-muted-fg/60 focus:outline-none focus:border-pos transition resize-y leading-relaxed"
              />
              <div className="text-[9px] text-muted-fg/70 text-right mt-1">{message.length}/4000</div>
            </div>

            {/* Identity preview */}
            <div className="text-[10px] text-muted-fg border-l-2 border-subtle pl-3">
              Sending as <span className="text-secondary-fg">{user.name || "—"}</span>
              {" "}&lt;{user.email}&gt; · I'll reply directly to this address.
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="feedback-submit"
                className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest border border-pos bg-pos text-black rounded-sm hover:bg-pos/90 disabled:opacity-60 flex items-center gap-1.5 transition"
              >
                {submitting && <CircleNotch size={12} className="animate-spin" />}
                Send Feedback
              </button>
              <Link
                to="/dashboard"
                className="px-4 py-2 text-[11px] font-mono uppercase tracking-widest border border-subtle text-secondary-fg rounded-sm hover:bg-surface-hover transition"
              >
                Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
